import { FC, FormEvent, useEffect, useState } from "react";
import { useFetcher } from "react-router";
import {
  Badge,
  Button,
  ButtonList,
  Input,
  Select,
  Text,
} from "@postenbring/hedwig-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import {
  USER_ROLES,
  UserOrganizationAccess,
  UserRole,
} from "../infrastructure/api/api";
import Translated from "../infrastructure/translation/Translated";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import { TranslationKey } from "../infrastructure/translation/translator";
import {
  buildAddUserPayload,
  buildDeleteUserPayload,
  UserActionErrorReason,
  UsersActionResult,
  useUsersRouteData,
} from "./usersRouteContext";
import styles from "./UsersPage.module.css";

const submitOptions = {
  method: "post" as const,
  encType: "application/json" as const,
};

const ADD_ERROR_KEYS: Partial<Record<UserActionErrorReason, TranslationKey>> = {
  invalid_nin: "users.add_error_invalid_nin",
  role_required: "users.add_error_role_required",
  already_has_access: "users.add_error_already_has_access",
};

const DELETE_ERROR_KEYS: Partial<
  Record<UserActionErrorReason, TranslationKey>
> = {
  cannot_delete_self: "users.delete_error_self",
  user_not_found: "users.delete_error_not_found",
};

const errorTextKey = (
  result: UsersActionResult | undefined,
  intent: "add" | "delete",
  keysByReason: Partial<Record<UserActionErrorReason, TranslationKey>>,
  fallbackKey: TranslationKey,
): TranslationKey | undefined => {
  if (!result || "ok" in result || result.intent !== intent) {
    return undefined;
  }

  return keysByReason[result.reason] ?? fallbackKey;
};

const RoleBadge: FC<{ role: UserRole }> = ({ role }) => (
  <Badge data-color={role === "OWNER" ? "success" : "neutral"} size="smaller">
    <Translated textKey={`users.role_${role}`} />
  </Badge>
);

const AddUserForm: FC = () => {
  const translate = useTranslate();
  const fetcher = useFetcher<UsersActionResult>();
  const [nin, setNin] = useState("");
  const [role, setRole] = useState<UserRole>("ADMIN");

  const submitting = fetcher.state !== "idle";
  const result = fetcher.data;
  const errorKey = errorTextKey(
    result,
    "add",
    ADD_ERROR_KEYS,
    "users.add_error",
  );

  useEffect(() => {
    if (fetcher.state === "idle" && result && "ok" in result) {
      setNin("");
      setRole("ADMIN");
    }
  }, [fetcher.state, result]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = nin.trim();
    if (!trimmed) {
      return;
    }
    fetcher.submit(buildAddUserPayload(trimmed, role), submitOptions);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.addForm}>
      <div className={styles.addFormHeader}>
        <Text variant="h3" as="h3" size="min">
          <Translated textKey={"users.add_header"} />
        </Text>
        <Text variant="body-small">
          <Translated textKey={"users.add_explanation"} />
        </Text>
      </div>
      <div className={styles.addFormFields}>
        <div className={styles.ninField}>
          <Input
            label={translate("users.nin_label")}
            value={nin}
            onChange={(e) => setNin(e.target.value)}
            inputMode="numeric"
            autoComplete="off"
            required
          />
        </div>
        <div className={styles.roleField}>
          <Select
            label={translate("users.role_label")}
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
          >
            {USER_ROLES.map((r) => (
              <option key={r} value={r}>
                {translate(`users.role_${r}`)}
              </option>
            ))}
          </Select>
        </div>
        <Button
          type="submit"
          className={styles.addButton}
          disabled={submitting}
        >
          <Translated
            textKey={submitting ? "users.adding" : "users.add_button"}
          />
        </Button>
      </div>
      {errorKey && (
        <p className={styles.error} role="alert">
          <Translated textKey={errorKey} />
        </p>
      )}
    </form>
  );
};

const UserRow: FC<{
  user: UserOrganizationAccess;
  canManage: boolean;
}> = ({ user, canManage }) => {
  const fetcher = useFetcher<UsersActionResult>();
  const [confirming, setConfirming] = useState(false);

  const deleting = fetcher.state !== "idle";
  const result = fetcher.data;
  const errorKey = errorTextKey(
    result,
    "delete",
    DELETE_ERROR_KEYS,
    "users.delete_error",
  );

  const handleDelete = () => {
    fetcher.submit(buildDeleteUserPayload(user.nin), submitOptions);
  };

  return (
    <li className={styles.row}>
      <span className={styles.rowIcon}>
        <FontAwesomeIcon icon={faUser} aria-hidden />
      </span>
      <div className={styles.rowContent}>
        <span className={styles.rowNin}>{user.nin}</span>
        <RoleBadge role={user.role} />
        {errorKey && (
          <p className={styles.error} role="alert">
            <Translated textKey={errorKey} />
          </p>
        )}
      </div>

      {canManage && (
        <span className={styles.rowActions}>
          {confirming ? (
            <ButtonList>
              <Button
                size="small"
                data-color="danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                <Translated
                  textKey={deleting ? "users.deleting" : "users.delete_confirm"}
                />
              </Button>
              <Button
                size="small"
                variant="secondary"
                type="button"
                onClick={() => setConfirming(false)}
                disabled={deleting}
              >
                <Translated textKey={"generic.cancel"} />
              </Button>
            </ButtonList>
          ) : (
            <Button
              size="small"
              variant="secondary"
              type="button"
              onClick={() => setConfirming(true)}
            >
              <Translated textKey={"users.delete"} />
            </Button>
          )}
        </span>
      )}
    </li>
  );
};

export const UsersPage: FC = () => {
  const { users, canManageUsers } = useUsersRouteData();

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <Text variant="h3" as="h2" size="min">
          <Translated textKey={"users.header"} />
        </Text>
        <Text variant="body-small">
          <Translated
            textKey={
              canManageUsers
                ? "users.description_owner"
                : "users.description_readonly"
            }
          />
        </Text>
      </div>

      {users.length === 0 ? (
        <p className={styles.emptyState}>
          <Translated textKey={"users.empty"} />
        </p>
      ) : (
        <ul className={styles.list}>
          {users.map((user) => (
            <UserRow key={user.nin} user={user} canManage={canManageUsers} />
          ))}
        </ul>
      )}

      {canManageUsers && <AddUserForm />}
    </section>
  );
};
